import { useMemo, useState } from 'react';

import { CatalogueFilters } from './CatalogueFilters';
import { CatalogueTable } from './CatalogueTable';
import { useNotificationCatalogue } from './useNotificationCatalogue';
import { LayoutCard, Stack, Typography } from '@cast/design-system';

import { DocsTextButton } from '../_components';

// Placeholder until the public notifications docs page exists.
const CATALOGUE_DOCS_URL = 'https://docs.cast.ai';

/** Severity filter options in meaningful order; unknown severities last, alphabetical. */
const SEVERITY_ORDER = ['critical', 'error', 'warning', 'info', 'success'];

const sortSeverities = (severities: string[]): string[] =>
  [...severities].sort((a, b) => {
    const rankA = SEVERITY_ORDER.indexOf(a.toLowerCase());
    const rankB = SEVERITY_ORDER.indexOf(b.toLowerCase());
    if (rankA !== rankB) {
      return (
        (rankA === -1 ? Infinity : rankA) - (rankB === -1 ? Infinity : rankB)
      );
    }
    return a.localeCompare(b);
  });

export const CatalogueView = () => {
  const { items, isLoading } = useNotificationCatalogue();
  const [search, setSearch] = useState('');
  const [selectedDomain, setSelectedDomain] = useState<string | null>(null);
  const [selectedSeverity, setSelectedSeverity] = useState<string | null>(null);

  // Domain and severity filter options are server data, not static config.
  const domainOptions = useMemo(
    () => Array.from(new Set(items.map((item) => item.domain))).sort(),
    [items]
  );
  const severityOptions = useMemo(
    () => sortSeverities(Array.from(new Set(items.map((i) => i.severity)))),
    [items]
  );

  const filteredItems = useMemo(() => {
    return items
      .filter((item) => {
        const matchesSearch =
          !search ||
          item.name.toLowerCase().includes(search.toLowerCase()) ||
          item.description.toLowerCase().includes(search.toLowerCase()) ||
          item.domain.toLowerCase().includes(search.toLowerCase());

        const matchesDomain = !selectedDomain || item.domain === selectedDomain;

        const matchesSeverity =
          !selectedSeverity || item.severity === selectedSeverity;

        return matchesSearch && matchesDomain && matchesSeverity;
      })
      .sort((a, b) => a.domain.localeCompare(b.domain));
  }, [items, search, selectedDomain, selectedSeverity]);

  return (
    <Stack height="100%" data-testid="notifications-catalogue-view">
      <LayoutCard padding={false} testId="catalogue-view-card">
        <Stack px="16px" pt="16px">
          <Typography
            variant="P14R"
            sx={{
              color: (theme) => theme.palette.text.secondary,
              mb: '12px',
            }}
          >
            All notifications Cast AI can generate, in one place — see what each
            one means and what triggers it.{' '}
            <DocsTextButton href={CATALOGUE_DOCS_URL} />
          </Typography>
          <CatalogueFilters
            domainOptions={domainOptions}
            severityOptions={severityOptions}
            search={search}
            onSearchChange={setSearch}
            selectedDomain={selectedDomain}
            onDomainChange={setSelectedDomain}
            selectedSeverity={selectedSeverity}
            onSeverityChange={setSelectedSeverity}
          />
        </Stack>
        <Stack px="16px" pb="16px">
          <CatalogueTable items={filteredItems} isLoading={isLoading} />
        </Stack>
      </LayoutCard>
    </Stack>
  );
};
